import {
  SystemStyleObject,
  TableCellProps as ChakraTableCellProps,
  TableColumnHeaderProps as ChakraTableHeaderProps,
} from "@chakra-ui/react";
import {
  AggregationFn,
  Cell,
  RowData,
  IdentifiedColumnDef,
  CellContext,
  TableMeta,
  HeaderContext,
  Row,
} from "@tanstack/react-table";
import { useRef } from "react";
import type { ColumnParams, ComponentProps } from ".";

function getLatest<T>(property: string, prev: T, current: T) {
  if (prev && current) {
    // eslint-disable-next-line no-console
    console.warn(
      `Attempting to set the \`${property}\` multiple times! ` +
        "Only using the most recent version.",
    );
  }
  return current || prev;
}

function mergeProps<P extends { sx?: SystemStyleObject }>(
  a: P | undefined,
  b: P | undefined,
): P {
  return {
    ...a,
    ...b,
    sx: a?.sx || b?.sx ? { ...a?.sx, ...b?.sx } : undefined,
  } as P;
}

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    cellProps?: Omit<ChakraTableCellProps, "key">;
    headerProps?: Omit<ChakraTableHeaderProps, "key">;
    footerProps?: Omit<ChakraTableCellProps, "key">;
    pinned?: "left" | "right";
    props?: Omit<ChakraTableHeaderProps & ChakraTableCellProps, "key">;
    getCellProps?: (
      cell: Cell<TData, TValue>,
      columnParams: ColumnParams,
    ) => Omit<ChakraTableCellProps, "key">;
    getColSpan?: (cell: Cell<TData, TValue>) => number;
  }
}

export function useChakraReactTableProps<D extends RowData>(
  propsArray: (Partial<ComponentProps<D>> | null | undefined)[],
): Partial<ComponentProps<D>> {
  return propsArray.reduce<Partial<ComponentProps<D>>>(
    (prev, props) => ({
      colgroupElement: getLatest(
        "colgroupElement",
        prev.colgroupElement,
        props?.colgroupElement,
      ),
      colSpanCalculator: getLatest(
        "colSpanCalculator",
        prev.colSpanCalculator,
        props?.colSpanCalculator,
      ),
      loadingSpinnerProps: mergeProps(
        prev.loadingSpinnerProps,
        props?.loadingSpinnerProps,
      ),
      tableBodyProps: mergeProps(prev.tableBodyProps, props?.tableBodyProps),
      tableHeadProps: mergeProps(prev.tableHeadProps, props?.tableHeadProps),
      tableFootProps: mergeProps(prev.tableFootProps, props?.tableFootProps),
      tableProps: mergeProps(prev.tableProps, props?.tableProps),
      getCellProps: (cell, columnParams) =>
        mergeProps(
          prev.getCellProps?.(cell, columnParams),
          props?.getCellProps?.(cell, columnParams),
        ),
      getColumnProps: (column, columnParams) =>
        mergeProps(
          prev.getColumnProps?.(column, columnParams),
          props?.getColumnProps?.(column, columnParams),
        ),
      getHeaderGroupProps: (headerGroup) =>
        mergeProps(
          prev.getHeaderGroupProps?.(headerGroup),
          props?.getHeaderGroupProps?.(headerGroup),
        ),
      getHeaderProps: (header, columnParams) =>
        mergeProps(
          prev.getHeaderProps?.(header, columnParams),
          props?.getHeaderProps?.(header, columnParams),
        ),
      getFooterGroupProps: (footerGroup) =>
        mergeProps(
          prev.getFooterGroupProps?.(footerGroup),
          props?.getFooterGroupProps?.(footerGroup),
        ),
      getFooterProps: (footer, columnParams) =>
        mergeProps(
          prev.getFooterProps?.(footer, columnParams),
          props?.getFooterProps?.(footer, columnParams),
        ),
      getRowProps: (row) =>
        mergeProps(prev.getRowProps?.(row), props?.getRowProps?.(row)),
      noRowsPlaceholderProps: mergeProps(
        prev.noRowsPlaceholderProps,
        props?.noRowsPlaceholderProps,
      ),
      noRowsPlaceholderRowProps: mergeProps(
        prev.noRowsPlaceholderRowProps,
        props?.noRowsPlaceholderRowProps,
      ),
    }),
    {},
  );
}

export function makeNotReadonly<D extends RowData>(
  data: readonly D[] | D[],
): D[] {
  return data as unknown as D[];
}

export function usePrepApolloData<D extends RowData>(
  data: readonly D[] | D[] | null | undefined,
): D[] {
  const { current: defaultData } = useRef<D[]>([]);
  if (!data) return defaultData;
  return makeNotReadonly(data);
}

/**
 * The `createColumnHelper` function from `@tanstack/react-table` attempts to
 * recurse objects to find all possible keys, which causes problems when row
 * data contain more complex objects, leading TS to crash. This avoids such
 * crashes by not recursing objects and consequently only supports keys that are
 * one level deep.
 */
export function createFlatAccessorKeyColumnHelper<TData extends RowData>() {
  return <TAccessor extends keyof TData, TValue extends TData[TAccessor]>(
    accessor: TAccessor,
    column: IdentifiedColumnDef<TData, TValue>,
  ) => ({ ...column, accessorKey: accessor });
}

/**
 * The `createColumnHelper` function from `@tanstack/react-table` attempts to
 * recurse objects to find all possible keys, which causes problems when row
 * data contain more complex objects, leading TS to crash. This avoids such
 * crashes by not recursing objects and consequently only supports keys that are
 * one level deep.
 */
export function createFlatAccessorFnColumnHelper<TData extends RowData>() {
  return <TValue>(
    accessor: (row: TData) => TValue,
    column: IdentifiedColumnDef<TData, TValue> & { id: string },
  ) => ({ ...column, accessorFn: accessor });
}

export function createTableMetaHelper<
  D extends RowData,
  TMeta extends TableMeta<D> | undefined = TableMeta<D> | undefined,
>() {
  return {
    cell: <DCell extends RowData = D, TValue = unknown>(
      cell: CellContext<DCell, TValue> | HeaderContext<DCell, TValue>,
    ) => cell.table.options.meta as TMeta,
  };
}

export const pinnedColumnStyles = {
  '[data-pinning="left"][data-pinning-index-inside="0"]': {
    boxShadow: "inset -1px 0px 0px rgba(0,0,0,.1)",
  },
  '[data-pinning="right"]': {
    boxShadow: "inset 1px 0px 0px rgba(0,0,0,.1)",
    borderRight: 0,
  },
};

export function combineWithPinnedColumnBoxShadow(
  boxShadow: string,
  prefix?: string,
): ChakraTableCellProps {
  const withPinnedStyles = Object.keys(pinnedColumnStyles).reduce<{
    [property: string]: SystemStyleObject;
  }>((acc, selector) => {
    const pinnedStyles =
      pinnedColumnStyles[selector as keyof typeof pinnedColumnStyles];
    if (!pinnedStyles.boxShadow) return acc;
    acc[`${prefix}&${selector}`] = {
      boxShadow: `${boxShadow}, ${pinnedStyles.boxShadow}`,
    };
    return acc;
  }, {});

  return { boxShadow, sx: withPinnedStyles };
}

export function getTotalWithAggregationFn<D extends RowData, TValue>(
  info: HeaderContext<D, TValue>,
  aggregationFn: AggregationFn<D>,
) {
  const { rows } = info.table.getRowModel();

  const childRows = rows.filter((row) => row.depth === 0);

  const leafRows = childRows.reduce((acc, row) => {
    if (row.subRows.length) {
      acc.push(...row.getLeafRows());
    } else {
      acc.push(row);
    }
    return acc;
  }, [] as Row<D>[]);

  return aggregationFn(info.column.id, leafRows, childRows) as unknown;
}

export function getTotalAggregation<D extends RowData, TValue>(
  info: HeaderContext<D, TValue>,
) {
  const aggregationFn = info.column.getAggregationFn();
  if (!aggregationFn) return;
  return getTotalWithAggregationFn<D, TValue>(info, aggregationFn);
}
